import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, DatePicker, Button, Space, Typography, Divider,
  Tag, Alert, Table, message, Spin, Row, Col, Card, Badge, Tooltip, Tabs, Checkbox
} from 'antd';
import {
  SaveOutlined, DeleteOutlined, CheckOutlined, CloseOutlined, WarningOutlined,
  UserOutlined, TeamOutlined, StarOutlined, ClockCircleOutlined
} from '@ant-design/icons';
import { appointmentApi, Appointment, AppointmentConflict, AppointmentPart } from '../../services/appointmentApi';
import { getTokens } from '../../services/authApi';
import axios from 'axios';
import dayjs, { Dayjs } from 'dayjs';
import './EnhancedAppointmentModal.css';

const { Title, Text } = Typography;
const { Option } = Select;
const { TextArea } = Input;
const { TabPane } = Tabs;

interface EnhancedAppointmentModalProps {
  visible: boolean;
  appointment: Appointment | null;
  onClose: () => void;
  defaultDate?: Dayjs;
  defaultTechnicianId?: number;
  estimateId?: number; // For creating appointment from estimate
}

interface User {
  id: number;
  first_name: string;
  last_name: string;
  role: string;
}

interface Customer {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
}

interface Vehicle {
  id: number;
  make: string;
  model: string;
  year: number;
  vin: string;
  customer_id: number;
}

interface SmartSlot {
  technician_id: number;
  technician_name: string;
  start_time: string;
  end_time: string;
  score: number;
  current_workload_percent: number;
  recommended: boolean;
}

export const EnhancedAppointmentModal: React.FC<EnhancedAppointmentModalProps> = ({
  visible,
  appointment,
  onClose,
  defaultDate,
  defaultTechnicianId,
  estimateId
}) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [technicians, setTechnicians] = useState<User[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState<number | null>(null);
  const [conflicts, setConflicts] = useState<AppointmentConflict[]>([]);
  const [parts, setParts] = useState<AppointmentPart[]>([]);
  const [showConflicts, setShowConflicts] = useState(false);
  const [checkingConflicts, setCheckingConflicts] = useState(false);

  // Multi-tech support
  const [primaryTechId, setPrimaryTechId] = useState<number | null>(defaultTechnicianId || null);
  const [additionalTechIds, setAdditionalTechIds] = useState<number[]>([]);

  // Smart suggestions
  const [smartSlots, setSmartSlots] = useState<SmartSlot[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const isEditMode = !!appointment?.id;

  useEffect(() => {
    if (visible) {
      loadTechnicians();
      loadCustomers();
      if (appointment) {
        loadAppointmentDetails();
      } else {
        resetForm();
      }
      if (estimateId) {
        loadEstimateData();
      }
    }
  }, [visible, appointment, estimateId]);

  useEffect(() => {
    if (selectedCustomerId) {
      loadCustomerVehicles(selectedCustomerId);
    } else {
      setVehicles([]);
    }
  }, [selectedCustomerId]);

  const resetForm = () => {
    form.setFieldsValue({
      appointment_type: 'diagnosis',
      status: 'scheduled',
      duration_minutes: 60,
      travel_time_minutes: 0,
      buffer_time_minutes: 15,
      manual_override: false,
      start_time: defaultDate || dayjs().add(1, 'day').hour(9).minute(0)
    });
    setPrimaryTechId(defaultTechnicianId || null);
    setAdditionalTechIds([]);
    setConflicts([]);
    setParts([]);
    setShowConflicts(false);
    setSmartSlots([]);
  };

  const loadEstimateData = async () => {
    if (!estimateId) return;

    try {
      const response = await axios.get(`/api/estimates/${estimateId}`);
      const estimate = response.data;

      // Pre-fill from estimate
      form.setFieldsValue({
        customer_id: estimate.customer_id,
        vehicle_id: estimate.vehicle_id,
        estimate_id: estimateId,
        appointment_type: 'repair',
        title: `Repair: ${estimate.estimate_number}`,
        description: estimate.customer_notes || ''
      });

      setSelectedCustomerId(estimate.customer_id);

      // Load smart suggestions for this estimate
      loadSmartSuggestionsForEstimate(estimateId);
    } catch (error) {
      console.error('Error loading estimate data:', error);
      message.error('Failed to load estimate data');
    }
  };

  const loadSmartSuggestionsForEstimate = async (estId: number) => {
    setLoadingSlots(true);
    try {
      const response = await axios.get(`/api/appointments/estimate/${estId}/availability`, {
        params: {
          max_results: 10
        }
      });

      setSmartSlots(response.data.suggested_slots || []);

      // Auto-fill duration from estimate
      if (response.data.required_duration_minutes) {
        form.setFieldValue('duration_minutes', response.data.required_duration_minutes);
      }
    } catch (error) {
      console.error('Error loading smart suggestions:', error);
    } finally {
      setLoadingSlots(false);
    }
  };

  const loadAppointmentDetails = async () => {
    if (!appointment?.id) return;

    setLoading(true);
    try {
      const fullAppointment = await appointmentApi.getById(appointment.id);

      form.setFieldsValue({
        ...fullAppointment,
        start_time: dayjs(fullAppointment.start_time),
        end_time: dayjs(fullAppointment.end_time)
      });

      setSelectedCustomerId(fullAppointment.customer_id);

      // Load multi-tech assignments
      if (fullAppointment.technicians && fullAppointment.technicians.length > 0) {
        const primary = fullAppointment.technicians.find(t => t.is_primary);
        const additional = fullAppointment.technicians
          .filter(t => !t.is_primary)
          .map(t => t.technician_id);

        setPrimaryTechId(primary?.technician_id || fullAppointment.technician_id);
        setAdditionalTechIds(additional);
      } else {
        setPrimaryTechId(fullAppointment.technician_id);
      }

      if (fullAppointment.conflicts) {
        setConflicts(fullAppointment.conflicts);
        setShowConflicts(fullAppointment.has_conflict);
      }
      if (fullAppointment.parts) {
        setParts(fullAppointment.parts);
      }
    } catch (error) {
      console.error('Error loading appointment details:', error);
      message.error('Failed to load appointment details');
    } finally {
      setLoading(false);
    }
  };

  const loadTechnicians = async () => {
    try {
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      const [techniciansResponse, ownersResponse] = await Promise.all([
        axios.get('/api/users', {
          params: { role: 'technician', status: 'active' },
          headers
        }),
        axios.get('/api/users', {
          params: { role: 'owner', status: 'active' },
          headers
        })
      ]);

      const allUsers = [...techniciansResponse.data, ...ownersResponse.data].sort((a, b) => {
        const nameA = `${a.first_name} ${a.last_name}`.toLowerCase();
        const nameB = `${b.first_name} ${b.last_name}`.toLowerCase();
        return nameA.localeCompare(nameB);
      });

      setTechnicians(allUsers);
    } catch (error) {
      console.error('Error loading technicians:', error);
    }
  };

  const loadCustomers = async () => {
    try {
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      const response = await axios.get('/api/customers', {
        params: { status: 'active' },
        headers
      });
      setCustomers(response.data);
    } catch (error) {
      console.error('Error loading customers:', error);
    }
  };

  const loadCustomerVehicles = async (customerId: number) => {
    try {
      const tokens = getTokens();
      const headers = tokens?.accessToken ? {
        Authorization: `Bearer ${tokens.accessToken}`
      } : {};

      const response = await axios.get(`/api/customers/${customerId}/vehicles`, {
        headers
      });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleSmartSlotSelect = (slot: SmartSlot) => {
    form.setFieldsValue({
      start_time: dayjs(slot.start_time)
    });
    setPrimaryTechId(slot.technician_id);
    message.success(`Selected ${slot.technician_name} at ${dayjs(slot.start_time).format('MMM DD, h:mm A')}`);
  };

  const handleSave = async (values: any) => {
    if (!primaryTechId) {
      message.error('Please select a primary technician');
      return;
    }

    setSaving(true);
    try {
      const durationMinutes = values.duration_minutes;
      const startTime = values.start_time.toISOString();
      const endTime = values.start_time.clone().add(durationMinutes, 'minutes').toISOString();

      const appointmentData: any = {
        ...values,
        technician_id: primaryTechId,
        start_time: startTime,
        end_time: endTime,
        manual_override: values.manual_override || false,
        has_conflict: conflicts.length > 0,
        conflict_acknowledged: values.manual_override && conflicts.length > 0
      };

      let appointmentId: number;

      if (isEditMode) {
        await appointmentApi.update(appointment.id!, appointmentData);
        appointmentId = appointment.id!;
        message.success('Appointment updated successfully');
      } else {
        const created = await appointmentApi.create(appointmentData);
        appointmentId = created.id!;
        message.success('Appointment created successfully');
      }

      // Handle multi-tech assignments
      if (additionalTechIds.length > 0) {
        const allTechIds = [primaryTechId, ...additionalTechIds];
        await axios.put(`/api/appointments/${appointmentId}/technicians`, {
          technician_ids: allTechIds,
          primary_technician_id: primaryTechId
        });
      }

      onClose();
    } catch (error: any) {
      console.error('Error saving appointment:', error);
      if (error.response?.status === 409) {
        message.error('Cannot save appointment due to conflicts. Enable manual override if necessary.');
      } else if (error.response?.data?.workflow_error === 'estimate_required') {
        message.error('Repair appointments require an estimate. Please create an estimate first.');
      } else {
        message.error('Failed to save appointment');
      }
    } finally {
      setSaving(false);
    }
  };

  const renderSmartSuggestions = () => {
    if (loadingSlots) {
      return (
        <div style={{ textAlign: 'center', padding: '20px' }}>
          <Spin tip="Finding best time slots..." />
        </div>
      );
    }

    if (smartSlots.length === 0) {
      return (
        <Alert
          message="No suggestions available"
          description="Try selecting a different date range or technician"
          type="info"
          showIcon
        />
      );
    }

    return (
      <div className="smart-suggestions-list">
        {smartSlots.map((slot, index) => (
          <Card
            key={index}
            className={`smart-slot-card ${slot.recommended ? 'recommended' : ''}`}
            hoverable
            onClick={() => handleSmartSlotSelect(slot)}
          >
            {slot.recommended && (
              <Tag color="gold" icon={<StarOutlined />} style={{ marginBottom: 8 }}>
                Recommended
              </Tag>
            )}
            <div className="slot-tech-name">
              <UserOutlined /> {slot.technician_name}
            </div>
            <div className="slot-time">
              <ClockCircleOutlined /> {dayjs(slot.start_time).format('MMM DD, YYYY @ h:mm A')}
            </div>
            <div className="slot-workload">
              <Badge
                status={
                  slot.current_workload_percent < 50 ? 'success' :
                  slot.current_workload_percent < 75 ? 'warning' : 'error'
                }
                text={`Workload: ${slot.current_workload_percent}%`}
              />
            </div>
            <div className="slot-score">
              Score: {slot.score}/100
            </div>
          </Card>
        ))}
      </div>
    );
  };

  return (
    <Modal
      title={isEditMode ? 'Edit Appointment' : 'New Appointment'}
      open={visible}
      onCancel={onClose}
      width={1000}
      footer={null}
      className="enhanced-appointment-modal"
    >
      <Spin spinning={loading}>
        <Tabs defaultActiveKey="details">
          <TabPane tab="Appointment Details" key="details">
            <Form
              form={form}
              layout="vertical"
              onFinish={handleSave}
            >
              {/* Estimate Link Warning */}
              {estimateId && (
                <Alert
                  message="Booking from Estimate"
                  description={`This appointment is linked to estimate #${estimateId}`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              {/* Basic Information */}
              <Title level={5}>Basic Information</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Customer"
                    name="customer_id"
                    rules={[{ required: true, message: 'Please select customer' }]}
                  >
                    <Select
                      showSearch
                      placeholder="Select customer"
                      onChange={setSelectedCustomerId}
                      filterOption={(input, option) => {
                        const label = `${option?.label || ''}`.toLowerCase();
                        return label.includes(input.toLowerCase());
                      }}
                      options={customers.map(customer => ({
                        value: customer.id,
                        label: `${customer.first_name} ${customer.last_name} - ${customer.phone}`
                      }))}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Vehicle"
                    name="vehicle_id"
                    rules={[{ required: true, message: 'Please select vehicle' }]}
                  >
                    <Select
                      placeholder="Select vehicle"
                      disabled={!selectedCustomerId}
                    >
                      {vehicles.map(vehicle => (
                        <Option key={vehicle.id} value={vehicle.id}>
                          {vehicle.year} {vehicle.make} {vehicle.model}
                        </Option>
                      ))}
                    </Select>
                  </Form.Item>
                </Col>
              </Row>

              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Appointment Type"
                    name="appointment_type"
                    rules={[{ required: true, message: 'Please select type' }]}
                  >
                    <Select>
                      <Option value="diagnosis">Diagnosis</Option>
                      <Option value="repair">Repair</Option>
                      <Option value="inspection">Inspection</Option>
                      <Option value="pickup">Pickup</Option>
                      <Option value="delivery">Delivery</Option>
                    </Select>
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Title"
                    name="title"
                    rules={[{ required: true, message: 'Please enter title' }]}
                  >
                    <Input placeholder="e.g., Engine Diagnosis" />
                  </Form.Item>
                </Col>
              </Row>

              <Form.Item
                label="Description"
                name="description"
              >
                <TextArea rows={3} placeholder="Appointment details" />
              </Form.Item>

              <Divider />

              {/* Multi-Technician Assignment */}
              <Title level={5}>
                <TeamOutlined /> Technician Assignment
              </Title>

              <Form.Item label="Primary Technician" required>
                <Select
                  placeholder="Select primary technician"
                  value={primaryTechId}
                  onChange={setPrimaryTechId}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                  }
                  options={technicians.map(tech => ({
                    value: tech.id,
                    label: `${tech.first_name} ${tech.last_name} (${tech.role})`
                  }))}
                />
              </Form.Item>

              <Form.Item
                label="Additional Technicians (Optional)"
                help="For double-booking or team appointments"
              >
                <Select
                  mode="multiple"
                  placeholder="Select additional technicians"
                  value={additionalTechIds}
                  onChange={setAdditionalTechIds}
                  showSearch
                  filterOption={(input, option) =>
                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                  }
                  options={technicians
                    .filter(tech => tech.id !== primaryTechId)
                    .map(tech => ({
                      value: tech.id,
                      label: `${tech.first_name} ${tech.last_name} (${tech.role})`
                    }))}
                />
              </Form.Item>

              {additionalTechIds.length > 0 && (
                <Alert
                  message="Multi-Technician Appointment"
                  description={`This appointment will be assigned to ${additionalTechIds.length + 1} technician(s)`}
                  type="info"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              )}

              <Divider />

              {/* Scheduling */}
              <Title level={5}>Scheduling</Title>
              <Row gutter={16}>
                <Col span={12}>
                  <Form.Item
                    label="Start Time"
                    name="start_time"
                    rules={[{ required: true, message: 'Please select start time' }]}
                  >
                    <DatePicker
                      showTime
                      format="YYYY-MM-DD HH:mm"
                      style={{ width: '100%' }}
                      minuteStep={15}
                    />
                  </Form.Item>
                </Col>
                <Col span={12}>
                  <Form.Item
                    label="Duration (minutes)"
                    name="duration_minutes"
                    rules={[{ required: true, message: 'Please enter duration' }]}
                  >
                    <Input type="number" min={15} step={15} />
                  </Form.Item>
                </Col>
              </Row>

              {/* Notes */}
              <Form.Item
                label="Customer Notes"
                name="notes"
              >
                <TextArea rows={2} placeholder="Notes visible to customer" />
              </Form.Item>

              <Form.Item
                label="Internal Notes"
                name="internal_notes"
              >
                <TextArea rows={2} placeholder="Internal notes (not visible to customer)" />
              </Form.Item>

              <Divider />

              {/* Footer Actions */}
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={onClose}>Cancel</Button>
                <Button
                  type="primary"
                  htmlType="submit"
                  icon={<SaveOutlined />}
                  loading={saving}
                >
                  {isEditMode ? 'Update' : 'Create'} Appointment
                </Button>
              </Space>
            </Form>
          </TabPane>

          {/* Smart Suggestions Tab */}
          {!isEditMode && estimateId && (
            <TabPane tab={<><StarOutlined /> Smart Suggestions</>} key="suggestions">
              <Title level={5}>Recommended Time Slots</Title>
              <Text type="secondary">
                Based on technician availability, workload, and estimated duration
              </Text>
              <Divider />
              {renderSmartSuggestions()}
            </TabPane>
          )}
        </Tabs>
      </Spin>
    </Modal>
  );
};
